import { readErrorMessage, resolveCsrfToken, sendJsonMutation } from '../components/write-request.js';
import { extractElement } from './steam-import-review.js';
import { acquirePaginationPending, announcePaginationPosition } from '../components/pagination-feedback.js';
import { prefersReducedMotion } from '../components/motion-policy.js';

/**
 * The games picker of the Steam import.
 *
 * Selection lives in the database, so this module owns no state of its own: a checkbox sends one
 * intent and the count painted is the server's. That is what lets a selection survive search,
 * filtering, pagination and a second tab with nothing to reconcile.
 *
 * Everything else - a tab, a page, a search, `Select all` and `Clear selection` - is a read of the
 * screen (after a write, for the two bulk actions), and this module swaps one region out of the answer:
 * `[data-steam-picker-results]`. The search box stays outside it so typing is never interrupted.
 *
 * Nothing here is required. Without JavaScript the links navigate, the bulk actions post, and the page's
 * checkboxes go out together behind `Save selection`. A failed write is never sent again natively: the
 * failure is said outside the results (so it survives the re-read of the view that follows it), and the
 * view is read again for the server's selection.
 */
const RESULTS_SELECTOR = '[data-steam-picker-results]';
const WRITE_ERROR_SELECTOR = '[data-steam-picker-write-error]';
const SELECTION_REFUSED_MESSAGE = 'Could not save that selection.';
const SELECTION_UNKNOWN_MESSAGE = 'Could not confirm the selection was saved. Reload to see the current selection.';

/** How long the search waits after the last keystroke. Enter and `Search` do not wait. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * How far below the viewport the CTA under the grid counts as reached, so the sticky bar is gone before
 * it could sit over the last row: the pager stands between that row and the CTA.
 */
const STICKY_BAR_LEAD = '0px 0px 160px 0px';

export function initSteamImportPicker(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    timerApi = globalThis,
) {
    const root = documentRef?.querySelector?.('[data-steam-picker]');
    if (
        typeof fetchImpl !== 'function'
        || typeof documentRef?.addEventListener !== 'function'
        || !(root instanceof HTMLElement)
    ) {
        return;
    }

    /** The read in flight; a newer one calls it off, so an older answer never paints over a newer one. */
    let currentRead = null;

    /**
     * Every write of the selection - a checkbox and both bulk actions - runs through one queue, in the
     * order it was started, so the last intent always wins: a tick followed by `Clear selection` cannot
     * land after the clear. `writeGeneration` moves on every write queued; a read that started before it
     * is stale and is asked again once the queue is empty, because its answer shows the selection from
     * before that write. A read never aborts a write - an aborted POST can still commit.
     */
    let writeQueue = Promise.resolve();
    let writesQueued = 0;
    let writeGeneration = 0;

    const enqueueWrite = (run) => {
        writesQueued += 1;
        writeGeneration += 1;
        const task = writeQueue.then(run);
        writeQueue = task.then(() => {}, () => {}).finally(() => {
            writesQueued -= 1;
        });

        return task;
    };

    const writesSettled = async () => {
        while (writesQueued > 0) {
            await writeQueue;
        }
    };

    let searchTimer = null;

    const showWriteError = (message) => {
        const slot = documentRef.querySelector(WRITE_ERROR_SELECTOR);
        if (slot instanceof HTMLElement) {
            slot.textContent = message;
            slot.hidden = message === '';
        }
    };

    const paintSelected = (total) => {
        // Server-counted, never adjusted here: a counter kept in the browser is exactly the second
        // source of truth this design removes. The toolbar and the sticky bar are one state.
        if (!Number.isFinite(total)) {
            return;
        }

        const count = documentRef.querySelector('[data-steam-picker-selected-count]');
        if (count instanceof HTMLElement) {
            count.textContent = String(total);
        }

        const sticky = documentRef.querySelector('[data-steam-picker-sticky-count]');
        if (sticky instanceof HTMLElement) {
            sticky.textContent = `${total} ${total === 1 ? 'game' : 'games'} selected`;
        }
    };

    /**
     * One fetch of the screen and the swap of the results out of it. `isCurrent` is asked before the
     * swap, and a `false` leaves the screen alone (`stale`) for the caller to ask again.
     */
    const request = async (url, init, signal, isCurrent) => {
        const results = documentRef.querySelector(RESULTS_SELECTOR);
        if (!(results instanceof HTMLElement)) {
            return 'failed';
        }

        // Local to the results: the current grid stays on screen, dimmed, until its replacement lands.
        results.setAttribute('aria-busy', 'true');

        try {
            const response = await fetchImpl(url, {
                ...init,
                headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
                signal,
            });
            if (response?.ok !== true) {
                if (init.method !== 'POST') {
                    return 'failed';
                }

                // A write's refusal says why (the CSRF guard answers JSON with an `error`), told apart from
                // a write whose answer never came or cannot be read.
                showWriteError(await readErrorMessage(response, SELECTION_REFUSED_MESSAGE));

                return 'rejected';
            }

            const html = await response.text();
            if (signal?.aborted) {
                return 'superseded';
            }
            if (!isCurrent()) {
                return 'stale';
            }

            const incoming = extractElement(documentRef, html, RESULTS_SELECTOR);
            if (incoming === null) {
                return 'failed';
            }

            results.replaceWith(incoming);
            const filter = documentRef.querySelector('[data-steam-picker-search-filter]');
            if (filter instanceof HTMLInputElement) {
                filter.value = incoming.dataset?.steamPickerFilter ?? 'all';
            }
            paintSelected(Number(incoming.dataset?.steamPickerSelectedTotal));

            if (init.method === 'POST') {
                // A write answers with a redirect, and what it says - `12 more games selected`, or the
                // limiter's refusal - is a flash in the page header, consumed by this very render.
                const flashes = documentRef.querySelector('.page-flashes');
                const incomingFlashes = extractElement(documentRef, html, '.page-flashes');
                if (flashes instanceof HTMLElement && incomingFlashes !== null) {
                    flashes.replaceWith(incomingFlashes);
                }

                // Where the server actually sent it - a refusal lands on the picker's own address, not
                // on the view it was pressed on - so the address bar names the view now on screen.
                const landed = typeof response.url === 'string' ? response.url.split('#')[0] : '';
                if (landed !== '' && landed !== windowRef?.location?.href?.split('#')[0]) {
                    windowRef?.history?.replaceState?.(windowRef?.history?.state ?? null, '', landed);
                }
            }

            return 'ok';
        } catch (_error) {
            return signal?.aborted ? 'superseded' : 'failed';
        } finally {
            // A superseded request leaves the dimming to the one that replaced it.
            if (!signal?.aborted) {
                results.removeAttribute('aria-busy');
            }
        }
    };

    const load = async (url) => {
        currentRead?.abort();
        const controller = new AbortController();
        currentRead = controller;

        for (;;) {
            await writesSettled();
            if (controller.signal.aborted) {
                return 'superseded';
            }

            const generation = writeGeneration;
            const outcome = await request(url, {}, controller.signal, () => generation === writeGeneration);
            if (outcome !== 'stale') {
                return outcome;
            }
        }
    };

    /**
     * `Select all` and `Clear selection`, followed through the redirect to the re-rendered view. Its
     * answer is painted only when no write is queued behind it; otherwise - and after a failure, which is
     * said and never posted again natively - the view is read again once those writes have landed.
     */
    const bulkWrite = async (form) => {
        showWriteError('');
        const outcome = await enqueueWrite(() => request(
            form.getAttribute('action') ?? '',
            { method: 'POST', body: new FormData(form) },
            null,
            () => writesQueued === 1,
        ));
        if (outcome === 'failed') {
            showWriteError(SELECTION_UNKNOWN_MESSAGE);
        }
        if (outcome !== 'ok') {
            return load(windowRef?.location?.href ?? '');
        }

        return outcome;
    };

    /**
     * Where the reader is put after a swap. A page step lands just above the grid, on `Showing 61-120`;
     * a tab or a search on the top of the results - and only when that is above the viewport, so a
     * search typed right over the results does not move the page under the cursor. Never the top of
     * the page: every step would then walk back through the intro, the tabs and the toolbar.
     */
    const bringIntoView = (target) => {
        const behavior = prefersReducedMotion(windowRef) ? 'auto' : 'smooth';

        if (target === 'range') {
            const range = documentRef.querySelector('[data-steam-picker-range]')
                ?? documentRef.querySelector(RESULTS_SELECTOR);
            range?.focus?.({ preventScroll: true });
            range?.scrollIntoView?.({ block: 'start', behavior });

            return;
        }

        const results = documentRef.querySelector(RESULTS_SELECTOR);
        if ((results?.getBoundingClientRect?.().top ?? 0) < 0) {
            results.scrollIntoView?.({ block: 'start', behavior });
        }

        if (target === 'tab') {
            documentRef.querySelector('[data-steam-picker-current-tab]')?.focus?.({ preventScroll: true });
        }
    };

    const show = async (url, { history = 'push', target = 'results' } = {}) => {
        const outcome = await load(url);
        if (outcome === 'ok') {
            windowRef?.history?.[history === 'push' ? 'pushState' : 'replaceState']?.({ steamPicker: true }, '', url);
            bringIntoView(target);
            if (target === 'range') {
                const content = documentRef.querySelector('#steam-picker-content');
                announcePaginationPosition([content], {
                    pageLabel: content?.dataset?.paginationPageLabel,
                    currentPage: content?.dataset?.paginationCurrentPage,
                    totalPages: content?.dataset?.paginationTotalPages,
                });
            }

            return;
        }

        if (outcome === 'failed') {
            windowRef?.location?.assign?.(url);
        }
    };

    const search = (history) => {
        timerApi.clearTimeout(searchTimer);
        const form = documentRef.querySelector('[data-steam-picker-search]');
        if (form instanceof HTMLElement) {
            show(searchUrl(form), { history });
        }
    };

    /**
     * Every way off this screen leads to one that reads the selection, so leaving waits for every write
     * still on its way - a new tab included, which is a Cmd/Ctrl/Shift-click or a middle click
     * (`auxclick`). True when the event was a way off the screen, handled or left to the browser.
     */
    const leaveAfterWrites = (event) => {
        const onwards = event.target?.closest?.('[data-steam-picker-leave]');
        if (!(onwards instanceof HTMLElement)) {
            return false;
        }

        const target = onwards.getAttribute?.('href') ?? '';
        if (writesQueued === 0 || target === '' || event.defaultPrevented === true) {
            return true;
        }

        const wantsNewTab = event.metaKey === true || event.ctrlKey === true || event.shiftKey === true
            || event.button === 1;
        // Taken off the browser synchronously; only the navigation itself waits. A new tab is opened
        // blank right now, while the click still counts as a user activation - opened after the wait it
        // could be refused as a popup - and pointed at the target once the writes have landed. Refused
        // even now, the same tab goes instead: late is better than onto a screen that misses the write.
        event.preventDefault?.();
        const tab = wantsNewTab ? windowRef?.open?.('', '_blank') ?? null : null;
        if (tab !== null) {
            tab.opener = null;
        }
        writesSettled().then(() => {
            if (tab !== null) {
                tab.location.href = target;
            } else {
                windowRef?.location?.assign?.(target);
            }
        });

        return true;
    };

    documentRef.addEventListener('auxclick', (event) => {
        if (event.button === 1) {
            leaveAfterWrites(event);
        }
    });

    // Capture, so this runs before the shared pager's own delegated click and its `defaultPrevented`
    // check makes it stand down: a tab, a page and a search swap one region, and one owner is what keeps
    // two modules from painting into it.
    documentRef.addEventListener('click', (event) => {
        if (leaveAfterWrites(event)) {
            return;
        }

        if (isModifiedClick(event)) {
            return;
        }

        const tab = event.target?.closest?.('[data-steam-picker-tab]');
        const pageLink = event.target?.closest?.('[data-page-number]');
        const link = tab ?? pageLink;
        const href = link?.getAttribute?.('href');
        if (!(link instanceof HTMLElement) || !root.contains(link) || typeof href !== 'string' || href === '') {
            return;
        }

        event.preventDefault?.();
        const releasePending = link === pageLink
            ? acquirePaginationPending(documentRef.querySelector(RESULTS_SELECTOR), pageLink)
            : () => {};
        show(href, { target: link === tab ? 'tab' : 'range' }).finally(releasePending);
    }, { capture: true });

    documentRef.addEventListener('click', (event) => {
        const clear = event.target?.closest?.('[data-steam-picker-clear]');
        const field = documentRef.querySelector('#steam-picker-query');
        if (!(clear instanceof HTMLElement) || !(field instanceof HTMLInputElement)) {
            return;
        }

        field.value = '';
        clear.hidden = true;
        field.focus?.();
        search('push');
    });

    documentRef.addEventListener('input', (event) => {
        const field = event.target;
        if (!(field instanceof HTMLInputElement) || field.id !== 'steam-picker-query') {
            return;
        }

        const clear = documentRef.querySelector('[data-steam-picker-clear]');
        if (clear instanceof HTMLElement) {
            clear.hidden = field.value === '';
        }

        // The first answer of a typing run is a history entry, the rest refine it: `Back` returns to
        // before the search, not to every prefix of it.
        const history = new URLSearchParams(windowRef?.location?.search ?? '').get('q') ? 'replace' : 'push';
        timerApi.clearTimeout(searchTimer);
        searchTimer = timerApi.setTimeout(() => search(history), SEARCH_DEBOUNCE_MS);
    });

    documentRef.addEventListener('submit', async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLElement)) {
            return;
        }

        if (form.dataset?.steamPickerSearch !== undefined) {
            event.preventDefault?.();
            search('push');

            return;
        }

        if (form.dataset?.steamPickerBulk === undefined) {
            return;
        }

        event.preventDefault?.();
        await bulkWrite(form);
    });

    documentRef.addEventListener('change', async (event) => {
        const box = event.target;
        if (!(box instanceof HTMLInputElement) || box.dataset?.steamPickerChoice === undefined) {
            return;
        }

        // The state being asked for, never a toggle: two tabs ticking one card must agree.
        const included = box.checked === true;
        box.disabled = true;
        showWriteError('');
        const result = await enqueueWrite(() => sendJsonMutation(fetchImpl, {
            url: root.dataset?.steamPickerSelectUrl ?? '',
            csrfToken: resolveCsrfToken(documentRef),
            body: { steam_app_id: Number(box.value), included: included ? '1' : '0' },
            fallbackErrorMessage: SELECTION_REFUSED_MESSAGE,
            unknownErrorMessage: SELECTION_UNKNOWN_MESSAGE,
        }));
        box.disabled = false;

        if (!result.ok) {
            // Back to what it was, the failure said, and the view read again for the server's selection.
            box.checked = !included;
            showWriteError(result.errorMessage);
            await load(windowRef?.location?.href ?? '');

            return;
        }

        paintSelected(result.payload?.selectedTotal);
    });

    windowRef?.addEventListener?.('popstate', async () => {
        const href = windowRef?.location?.href;
        if (typeof href !== 'string' || href === '') {
            return;
        }

        const field = documentRef.querySelector('#steam-picker-query');
        if (field instanceof HTMLInputElement) {
            field.value = new URLSearchParams(windowRef?.location?.search ?? '').get('q') ?? '';
            const clear = documentRef.querySelector('[data-steam-picker-clear]');
            if (clear instanceof HTMLElement) {
                clear.hidden = field.value === '';
            }
        }

        const outcome = await load(href);
        if (outcome === 'failed') {
            // The address bar has already moved and the region could not follow it.
            windowRef?.location?.reload?.();
        }
    });

    initStickyBar(documentRef, windowRef);
}

/**
 * The mobile bar, shown only where it can also be hidden again: while the CTA under the grid is in view
 * the bar steps aside, so the two identical buttons never stand one above the other. The stylesheet
 * keeps it off desktop.
 */
function initStickyBar(documentRef, windowRef) {
    const bar = documentRef.querySelector('[data-steam-picker-sticky]');
    const actions = documentRef.querySelector('[data-steam-picker-actions]');
    if (!(bar instanceof HTMLElement) || !(actions instanceof HTMLElement)
        || typeof windowRef?.IntersectionObserver !== 'function') {
        return;
    }

    new windowRef.IntersectionObserver((entries) => {
        for (const entry of entries) {
            bar.classList.toggle('is-away', entry.isIntersecting === true);
        }
    }, { rootMargin: STICKY_BAR_LEAD }).observe(actions);
    bar.hidden = false;
}

/** The search form as the URL of the view it asks for; page dropped, defaults left out. */
export function searchUrl(form) {
    const filter = form.querySelector?.('[name="filter"]')?.value ?? 'all';
    const query = String(form.querySelector?.('[name="q"]')?.value ?? '').trim();
    const params = new URLSearchParams();
    if (filter !== '' && filter !== 'all') {
        params.set('filter', filter);
    }
    if (query !== '') {
        params.set('q', query);
    }

    const action = form.getAttribute?.('action') ?? '';
    const queryString = params.toString();

    return queryString === '' ? action : `${action}?${queryString}`;
}

function isModifiedClick(event) {
    return event?.defaultPrevented === true
        || event?.metaKey === true
        || event?.ctrlKey === true
        || event?.shiftKey === true
        || event?.altKey === true
        || (typeof event?.button === 'number' && event.button > 0);
}

initSteamImportPicker();
